/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Output, OnDestroy, Input, OnInit, computed } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { REGEX_PATTERNS } from 'src/app/models/enums/regex_patterns.enum';
import { Subject, catchError, filter, lastValueFrom, mergeMap, of, take, takeUntil } from 'rxjs';
import { StartFormData } from './start-form-data.type';
import { ActivatedRoute, Router } from '@angular/router';
import { AppModules, CreditRequestRoutes } from 'src/app/models/enums/routes.enum';
import { CreditErrorType } from 'src/app/models/enums/credit-error.enum';
import { GiveawayService } from 'src/app/services/giveaway.service';
import { DataService } from 'src/app/services/data.service';
import { CheckUrlDniResponse } from 'src/app/models/response/check-url-dni.response';
import { nifNieValidator } from 'src/app/validators/nif-nie.validator';
import { CreditService } from 'src/app/services/credit.service';
import { CodeVerificationService } from 'src/app/services/code-verification.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ErrorModalService } from 'src/app/services/error-modal.service';
import { SendCodeType } from 'src/app/models/response/send-verification-code.response';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from 'src/app/shared/reusable/button/button.component';
import { NgxCaptchaModule } from 'ngx-captcha';
import { environment } from 'src/app/enviroments/enviroment';
import { CaptchaValidationService } from 'src/app/services/captcha-validation.service';

@Component({
  selector: 'app-on-boarding-start',
  templateUrl: './on-boarding-start.component.html',
  styleUrls: ['./on-boarding-start.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, NgxCaptchaModule],
  standalone: true,
})
export class OnBoardingStartComponent implements OnDestroy, OnInit {
  @Output() returnStatus = new EventEmitter<StartFormData>();
  @Output() nextStep = new EventEmitter<boolean>();
  @Input() isModal = false;

  //captcha
  public captchaIsLoaded = false;
  public captchaSuccess = false;
  public captchaIsExpired = false;
  public captchaResponse?: string;
  public theme: 'light' | 'dark' = 'light';
  public size: 'compact' | 'normal' = 'normal';
  public lang = 'es';
  public type!: 'image' | 'audio';
  public sitekey = environment.googleCaptchaKey;

  private destroyRef$ = new Subject<boolean>();
  public loading = false;
  public existeCliente = false;

  public startForm = this.fb.group({
    dniNie: new FormControl<string | undefined>(undefined, {
      validators: [Validators.required, Validators.pattern(REGEX_PATTERNS.dniNie), nifNieValidator()],
      nonNullable: true,
    }),
  });

  public captchaValidated = computed(() => this.dataService.captchaToken() !== undefined);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private giveawayService: GiveawayService,
    private dataService: DataService,
    private creditService: CreditService,
    private codeService: CodeVerificationService,
    private spinner: SpinnerService,
    private modal: ErrorModalService,
    private route: ActivatedRoute,
    private captchaValidationService: CaptchaValidationService,
  ) {}

  ngOnInit(): void {
    this.checkStep();
  }

  private checkStep(): void {
    this.route.queryParamMap.subscribe((paramMap) => {
      const step = paramMap.get('step');
      if (step !== null && this.dataService.validatedDocument() !== undefined) this.existeCliente = true;
    });
  }

  public async onSubmit(): Promise<void> {
    this.loading = true;

    const { dniNie: dni } = this.startForm!.value;
    if (!dni) return;

    const link = await lastValueFrom(
      this.dataService
        .getIsGiveawayActive$()
        .pipe(
          mergeMap((active) =>
            active ? this.dataService.getGiveawayLink$().pipe(filter((link): link is string => !!link)) : of(''),
          ),
        )
        .pipe(take(1)),
    );

    if (link === '') this.validateClientExists(dni.trim());
    else {
      this.giveawayService
        .checkUrlDni({ link, dni })
        .pipe(
          takeUntil(this.destroyRef$),
          catchError(() => of<Partial<CheckUrlDniResponse>>({ valid: false })),
        )
        .subscribe((data) => {
          if (data.valid) this.validateClientExists(dni);
          else
            this.router.navigate([AppModules.CREDIT_REQUEST, CreditRequestRoutes.ERROR, CreditErrorType.GENERIC, '']);
        });
    }
  }

  validateClientExists(dni: string): void {
    if (!this.startForm) return;
    this.creditService
      .checkIfClientExists(dni)
      .pipe(takeUntil(this.destroyRef$))
      .subscribe(({ data: { existe_cliente, belongs_to_group } }) => {
        this.loading = false;
        this.dataService.updateUserData({ usuario: dni });
        if (existe_cliente) {
          this.existeCliente = true;
          if (belongs_to_group) {
            if (!this.isModal)
              this.modal.openModal({
                content: 'Premiamos a los buenos clientes y puede acceder a mejores beneficios',
                title: '¡Enhorabuena!',
                saveButtonLabel: 'Ver beneficios',
                closeButtonLabel: 'Continuar sin beneficios',
                url: '/',
              });

            this.dataService.setValidatedDocument(dni);
          }
        } else {
          if (this.isModal) {
            this.router.navigate([AppModules.CREDIT_REQUEST, CreditRequestRoutes.ONBOARDING]);
            //this.nextStep.emit(false);
            //this.showCodeError('No estás registrado en la plataforma continúa con el flujo normal', 'Error');
          } else {
            this.router.navigate([AppModules.CREDIT_REQUEST, CreditRequestRoutes.ONBOARDING]);
          }
        }
      });
  }

  sendCode(type: SendCodeType): void {
    const { dniNie: dni } = this.startForm!.value;
    const validatedDocument = this.dataService.validatedDocument();
    this.spinner.show('Enviando código de verificación...');
    lastValueFrom(this.codeService.sendCode({ dni: dni ?? validatedDocument!, type }))
      .then((codeData) => {
        if (codeData.status === 'error') {
          if (this.isModal) this.nextStep.emit(false);
          else this.showCodeError('Error al enviar código', 'Envío de código');
        } else {
          this.dataService.setCodeData(codeData);
          if (this.isModal) this.nextStep.emit(true);
          else this.router.navigate([AppModules.CREDIT_REQUEST, CreditRequestRoutes.ONBOARDING_START_LOGIN]);
        }
      })
      .catch(() => {
        this.showCodeError('Error al enviar código', 'Envío de código');
      })
      .finally(() => this.spinner.hide());
  }

  private showCodeError(content: string, title: string): void {
    this.modal.openModal({
      content,
      title,
    });
  }

  ngOnDestroy(): void {
    this.destroyRef$.next(true);
  }

  public handleCaptchaSuccess(token: string): void {
    this.captchaValidationService.validateCaptcha(token).subscribe((success) => {
      if (success) this.dataService.setCaptchaToken(token);
    });
  }

  public handleCaptchaExpired(): void {
    this.dataService.setCaptchaToken(undefined);
  }
}
